/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { selectAcRbeSynced, selectAcRbeSyncing, selectBoardUsersTaskRunning, selectCurrentDeviceInfo, selectCurrentDeviceStatus } from 'src/app/store/device/device.selector';
import { filter, Observable, Subscription } from 'rxjs';
import { DeviceInfo } from 'src/app/model/device-info';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { DeviceStatus } from 'src/app/model/device-status';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { User } from 'src/app/model/user';
import { selectCurrentLang, selectIsDraftMode, selectIsImpersMode, selectUser } from 'src/app/store/user/user.selector';
import { Lang } from 'src/app/model/lang';
import { DEFAULT_LANG } from 'src/app/conf/languages';
import { AppComponent } from 'src/app/app.component';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { ExportDeviceDialogComponent } from '../export-dialog/export-dialog.component';

@Component({
	selector: 'app-import-export-tab',
	templateUrl: './import-export-tab.component.html',
	providers: [ConfirmationService, DialogService]
})

export class ImportExportComponent implements OnInit, OnDestroy {
	deviceInfo: DeviceInfo;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;
	taskRunning$: Observable<boolean>;
	taskRunningSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	isImpersMode: boolean;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	isDraftMode: boolean;
	isDraftMode$: Observable<boolean | undefined>;
	isDraftModeSub: Subscription;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;

	acRbeSyncing: boolean;
	acRbeSyncing$: Observable<boolean | undefined>;
	acRbeSyncingSub: Subscription | undefined;

	acRbeSynced: boolean;
	acRbeSynced$: Observable<boolean | undefined>;
	acRbeSyncedSub: Subscription | undefined;

	online: boolean;
	taskRunning: boolean;

	ref: DynamicDialogRef | undefined;

	screenWidth: number;
	acceptButtonStyle: string;
	rejectButtonStyle: string;

	constructor(
		private store: Store<AppState>,
		private formBuilder: FormBuilder,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
		public app: AppComponent,
		public dialogService: DialogService
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.deviceInfo$ = this.store.select(selectCurrentDeviceInfo);
		this.taskRunning$ = this.store.select(selectBoardUsersTaskRunning);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.currentUser$ = this.store.select(selectUser);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
		this.isDraftMode$ = this.store.select(selectIsDraftMode);
		this.acRbeSyncing$ = this.store.select(selectAcRbeSyncing);
		this.acRbeSynced$ = this.store.select(selectAcRbeSynced);
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.onResize();
	}

	subscribeAll(): void {
		this.deviceInfoSub = this.deviceInfo$.pipe(filter(d => !!d))
			.subscribe(currentDeviceInfo => {
				this.deviceInfo = currentDeviceInfo;
			});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			if (deviceStatus !== undefined) {
				this.online = deviceStatus.Online;
			}
		});
		this.taskRunningSub = this.taskRunning$.subscribe(taskRunning => {
			this.taskRunning = taskRunning;
		});
		this.currentUserSub = this.currentUser$?.subscribe(currentUser => {
			this.currentUser = currentUser;
		});
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
		this.isDraftModeSub = this.isDraftMode$.subscribe(
			(isDraftMode) => {
				this.isDraftMode = isDraftMode;
			}
		);
		this.acRbeSyncingSub = this.acRbeSyncing$?.subscribe(acRbeSyncing => {
			this.acRbeSyncing = acRbeSyncing;
		});
		this.acRbeSyncedSub = this.acRbeSynced$?.subscribe(acRbeSynced => {
			this.acRbeSynced = acRbeSynced;
		});
	}

	ngOnDestroy(): void {
		this.deviceInfoSub?.unsubscribe();
		this.taskRunningSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
		this.isDraftModeSub?.unsubscribe();
		this.acRbeSyncingSub?.unsubscribe();
		this.acRbeSyncedSub?.unsubscribe();
	}

	compareVersions(ver1: string, ver2: string): boolean {
		// prevent missing versions v1 and/or v2
		if (!ver1) return false;
		if (!ver2) return true;

		const a = ver1.split('.').map(Number);
		const b = ver2.split('.').map(Number);

		const maxLength = Math.max(a.length, b.length);

		for (let i = 0; i < maxLength; i++) {
			const num1 = a[i] || 0; // if an element is missing, use 0 (default)
			const num2 = b[i] || 0;

			if (num1 > num2) return true;
			if (num1 < num2) return false;
		}

		return true; // if all parts are equal
	}

	exportData(): void {

		this.store.dispatch(UserFileActions.resetExportDeviceDialog());
		this.ref = this.dialogService.open(ExportDeviceDialogComponent, {
			header: this.translate.instant('EXPORT_DATA_FROM_DEVICE'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});


		/*this.acceptButtonStyle = "p-button-warning";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('EXPORT_DATA_DEVICE_TITLE'),
			message: this.translate.instant('EXPORT_DATA_DEVICE_TEXT'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.acRbeSync());
			},
			reject: () => {
			}
		});*/
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	col6ResponsiveButtonLayout(position: string): string {
		return StandardPageLayout.col6ResponsiveButtonLayout(this.screenWidth, position);
	}

	onExtendedMobile(): boolean {
		return (this.screenWidth <= 660);
	}
}